<template>
  <div>
    <p class="mt-4">
      {{ $t('processes.sidebar.main_features') }}
    </p>
    <table
      v-if="(record && record.ref) || isAssetProcess"
      class="table table-striped table-sm"
    >
      <tbody>
        <tr v-if="case_ref">
          <th scope="row">
            {{ $t('processes.sidebar.case_ref') }}
          </th>
          <td>{{ case_ref }}</td>
        </tr>
        <tr v-if="locationRef">
          <th scope="row">
            {{ $t('processes.sidebar.location_ref') }}
          </th>
          <td>{{ locationRef }}</td>
        </tr>
        <tr v-if="realisationLocationId">
          <th scope="row">
            {{ $t('processes.sidebar.location_id') }}
          </th>
          <td class="d-flex align-items-center">
            <i class="fa fa-link mr-1 router-link" />
            <router-link :to="{ name: 'LocationList', params: { locationId: realisationLocationId } }">
              {{ realisationLocationId }}
            </router-link>
          </td>
        </tr>
        <tr v-if="record.ref && chargingPointStatus">
          <th scope="row">
            {{ $t('processes.sidebar.chargingpoint_status') }}
          </th>
          <td>
            <img
              :src="statusImage"
              width="20"
            >
            {{ $t(`status_labels.${charginpointStatusLabel}`) }}
          </td>
        </tr>
        <tr>
          <th scope="row">
            {{ $t('processes.sidebar.address') }}
          </th>
          <td v-if="address !== '-'">
            {{ address }}
            <br>
            <a
              target="_blank"
              :href="`https://www.google.com/maps/search/?api=1&amp;query=${encodeURIComponent(address)}`"
            >
              {{ $t('processes.sidebar.open_in_google_maps') }} <i class="icon fa-fw fal fa-arrow-up-right-from-square" />
            </a>
          </td>
          <td v-else>
            {{ address }}
          </td>
        </tr>
        <tr v-if="StatusLabel">
          <th scope="row">
            {{ $t('processes.sidebar.process_status') }}
          </th>
          <td>{{ $t(StatusLabel) || $t('processes.headers.unknown') }}</td>
        </tr>
        <!-- <tr v-if="StatusDuration && ! missingDurationInformation"> -->
        <tr>
          <th scope="row">
            {{ $t('processes.sidebar.date_last_step') }}
          </th>
          <td :title="StatusDuration">
            {{ dateOfLastStep }}
          </td>
        </tr>
        <tr v-if="ChargePointOperator">
          <th scope="row">
            {{ $t('processes.sidebar.cpo') }}
          </th>
          <td>{{ ChargePointOperator }}</td>
        </tr>
        <tr v-if="contractor">
          <th scope="row">
            {{ $t('processes.sidebar.contractor') }}
          </th>
          <td>{{ contractor }}</td>
        </tr>
        <!-- park-n-charge specific...  -->
        <tr v-if="FinUnit">
          <th scope="row">
            {{ $t('processes.sidebar.spvfinunit') }}
          </th>
          <td>{{ FinUnit }}</td>
        </tr>
        <tr v-if="hasProcessType">
          <th scope="row">
            {{ $t('processes.sidebar.process_type') }}
          </th>
          <td v-if="isRealisationProcess">
            {{ $t(`processes.sidebar.process_categories.${processCategory}`) }}
          </td>
          <td v-else>
            {{ $t(`processes.sidebar.process_types.${processCategory}`) }}
          </td>
        </tr>
        <tr v-if="showTags">
          <th scope="row">
            {{ $t('processes.sidebar.tags') }}
          </th>
          <td>
            <span v-if="currentTags.length === 0">{{ $t('processes.sidebar.no_tags') }}</span>
            <b-badge
              v-for="(tag, index) in currentTags"
              :key="`tag_${index}`"
              class="mr-1"
              variant="secondary"
            >
              <div :title="labelByUuid({ uuid: tag })">
                {{ labelByUuid({ uuid: tag }) }}
              </div>
            </b-badge>
          </td>
        </tr>
      </tbody>
    </table>
    <b-alert
      v-else
      show
      variant="info"
    >
      {{ $t('processes.sidebar.save_location_proposal') }}
    </b-alert>
    <div v-if="record.ref">
      <div v-if="hasParkingSpots">
        <div class="startdocList">
          <h5>{{ $t('processes.sidebar.manual_startdocument') }}</h5>
          <i
            id="startdocTooltip"
            class="icon fa-fw far fa-circle-info"
          />
          <b-tooltip target="startdocTooltip">
            {{ $t('processes.sidebar.manual_startdocument_tooltip') }}
          </b-tooltip>
        </div>
        <FileUpload
          class=""
          label=""
          doctype="ParkingSpots"
          :uuid="uuid"
          :files="location && location.Docs ? location.Docs.ParkingSpots : null"
          :min-file-id="0"
          :reference="record.ref || null"
          :casefull="record.case_ref || null"
          :locked="true"
          :disabled="true"
        />
      </div>
      <template v-else>
        <DocDownload
          :uuid="uuid"
          class="mt-2"
        />
      </template>

      <slot />

      <RelatedRequests
        :record="record"
        class="mb-4"
      />
      <div v-if="case_ref && isNotCompleted && !getRecordStatusLocked">
        <RealisationStatusPanel
          :key="componentKey"
          class="mt-4 mb-4"
          :uuid="uuid"
          @update-component="componentKey++"
        />
        <RealisationVKBStatus
          class="mt-4 mb-4"
          :uuid="uuid"
        />
        <RealisationDelete
          class="my-4"
          :uuids="[uuid]"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FileUpload from '@/components/FileUpload';
import DocDownload from '@/components/realisation/DocDownload.vue';
import RealisationStatusPanel from '@/components/realisation/RealisationStatusPanel.vue';
import RealisationVKBStatus from '@/components/realisation/RealisationVKBStatus.vue';
import RelatedRequests from '@/components/realisation/RelatedRequests.vue';
import RealisationDelete from '@/components/realisation/RealisationDelete.vue';
import recordList from '@/helpers/recordList'
import { mapGetters } from 'vuex';

import { formatPostalCode } from '@/helpers/string';
import { msDurationToString, msToDate } from '@/helpers/date';
import { image } from '@/helpers/assets';

export default {
  name: 'RealisationSidebarMainTab',
  components: {
    FileUpload,
    RealisationStatusPanel,
    RealisationVKBStatus,
    DocDownload,
    RelatedRequests,
    RealisationDelete
  },
  props: {
    uuid: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      componentKey: 0,
      image,
      record: {},
      docs: {}
    };
  },
  computed: {
    ...mapGetters('realisations', ['getSelectedRecord', 'currentStep']),
    ...mapGetters('tenant', [
      'isCurrentTenant',
      'getEnabledTags',
      'getLabelsByTagUuid',
      'getSelectOptionLabelByValue',
      'getEnabledContractors'
    ]),
    ...mapGetters('user', ['hasRole', 'canAccessAssetManagement']),
    ...mapGetters('chargingpoints', ['getChargingPointByUUID']),
    ...mapGetters('processes', ['getSelectedProcess', 'getCurrentProcessType', 'getRecordStatusLocked']),
    ...mapGetters('realisation', ['getActiveRecordValues']),
    ...mapGetters('tenant', ['getSteps']),

    isNewProcess() {
      return this.$route.name.startsWith('new-')
    },
    currentRecord () {
      return this.isAssetProcess ? this.getSelectedProcess : this.getSelectedRecord
    },
    case_ref() {
      return !this.isNewProcess ? this.record?.case_ref : '-';
    },
    isNotCompleted() {
      return !this.record.status.completed // && !this.record.status.locked
    },
    isAssetProcess() {
      return this.getCurrentProcessType !== 'realisation'
    },
    realisationLocationId() {
      return this.canAccessAssetManagement && this.record.locationId?.full
    },
    isCompleted() {
      return this.record.status.completed
    },
    chargingPointStatus() {
      return (this.location.Location.chargingpointStatus || this.chargingPoint?.data?.properties?.status || '').trim();
    },
    statusImage() {
      return image({ name: `chargingpoint-${this.chargingPointStatus}` });
    },
    charginpointStatusLabel() {
      return this.chargingPointStatus || this.$t('status_labels.unknown');
    },
    location() {
      try {
        return this.record.values.Location;
      } catch (e) {
        return {
          Location: {}
        };
      }
    },
    locationRef() {
      return this.record.ref ? `${this.case_ref}-${this.location.version}` : '-';

    },
    contractor() {
      if (this.location.Contractor) {
        return this.getEnabledContractors.find(contractor => contractor.uuid === this.location.Contractor)?.label;
      }
      return false;
    },

    hasParkingSpots() {
      const location = this.record.CurrentLocation || this.location
      return location?.Docs?.ParkingSpots?.length
    },
    StatusLabel() {
      const currentStep = Number(this.record.status.step)
      const statusLabel = this.getSteps?.find(s => s.step === currentStep)?.label[this.$i18n.locale]
      return this.isCompleted ? this.$i18n.t('processes.statusFilters.completed') : statusLabel
    },
    StatusDuration() {
      try {
        let duration = this.record.getCurrentStepDuraction();
        return msDurationToString({ ms: Date.now() - duration.start });
      } catch (e) {
        return '';
      }
    },
    missingDurationInformation() {
      let duration = this.record.getCurrentStepDuraction();
      return duration.start === 0 || (duration.finished && duration.end === 0);
    },
    dateOfLastStep() {
      try {
        const duration = this.record.getCurrentStepDuraction();
        const lastStep = this.record.flow.state.filter(step => step.started_at).at(-1)?.started_at;
        const start = duration.start || lastStep;
        //console.log('start', start, duration, lastStep, this.record.flow.state)
        return msToDate({ ms: start });
      } catch (e) {
        return '-';
      }
    },
    ChargePointOperator() {
      try {
        const cpoUuid = this.record.values.Location.ChargePointOperator;
        return recordList.setCpo({ fieldValue: cpoUuid })
      } catch (e) {
        return false;
      }
    },
    chargingPoint() {
      const uuid = this.record.CurrentLocation.Location.chargingpointUuid;
      return this.getChargingPointByUUID({ uuid });
    },
    address() {
      const realisationData = this.record.realisationProcessData?.process
      const location = this.isRealisation ? this.record.CurrentLocation.Location : realisationData?.Location;

      if (!location?.StreetName) return '-'

      const number = `${location.HouseNumber || ''} ${location.HouseNumberSuffix || ''}`;

      return `${location.StreetName || ''} ${number.trim()}, ${formatPostalCode(location.PostalCode || '')} ${location.CityName || ''
        }`;
    },
    isRealisationProcess() {
      return this.record.processType.value === 'realisation'
    },
    hasProcessType() {
      return this.isAssetProcess || (this.isRealisationProcess && this.record?.hasCategory());
    },
    processCategory() {
      return (!this.isRealisationProcess && this.record.processType.value) ||
      this.record.getCategory()
    },
    isRealisation() {
      return this.getCurrentProcessType === 'realisation'
    },

    /**************************************************************************
     * TAGS
     */
    currentTags() {
      return this.record.getTags();
    },

    showTags() {
      return this.getEnabledTags.length || (this.record && this.record.hasTags());
    },

    /**************************************************************************
     * FIN UNIT - park-n-charge specific
     */
    FinUnit() {
      const location = this.record.CurrentLocation || this.record.values.Location
      return location.FinUnit

    }
  },
  watch: {
    currentRecord: {
      deep: true,
      immediate: true,
      handler (record) {
        if (!record) return;
        this.record = record
        this.docs = this.record.Docs
        // Refresh status component //
        this.componentKey++
      }
    },
    getRecordLocked: {
      handler (locked) {
        console.log(locked, this.record.status)
        this.componentKey++
      }
    }
  },

  methods: {
    getView (viewType) {
      return this.docs[viewType] || []
    },
    labelByUuid({ uuid }) {
      return this.getLabelsByTagUuid[uuid] || 'Onbekende tag';
    }
  }
};
</script>

<style lang="scss" scoped>
.startdocList {
  display: flex;
  align-items: center;

  >h5 {
    margin-right: 0.5rem;
    margin-bottom: 0;
  }
}

.Views {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  >div {
    width: 48%;
  }
}

tbody th {
  width: 52%;
}

.router-link {
  font-size: 0.8em;
  position: relative;
  top: 1px;
}
</style>
